import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { setFinancialAccessBlockedHandler } from "../lib/api";

// Correções de homologação (P2) — a liberação por confiança (48h)/baixa
// manual é feita pelo Admin Master, OUTRA sessão inteiramente — sem
// nenhum mecanismo de revalidação, o tenant já logado ficaria com
// financially_blocked=true "congelado" em memória até um logout/refresh
// manual, mesmo o backend já liberando o acesso a cada request novo desde
// o momento da concessão. Enquanto bloqueado, revalida silenciosamente em
// segundo plano (nunca precisa de F5/novo login para o usuário recuperar
// o acesso assim que for liberado).
const BLOCKED_REVALIDATE_MS = 12000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  // Planos/Recursos (Fundação Comercial) — plano contratado + recursos
  // efetivos (plano + overrides) da PRÓPRIA empresa do usuário logado,
  // usados por hasFeature() abaixo. null = admin_master (sem tenant) ou
  // ainda não carregado; nunca guarda dado de outra empresa.
  const [plan, setPlan] = useState(null);

  const loadPlan = useCallback(async (u) => {
    if (!u || !u.company_id) { setPlan(null); return; }
    try {
      const { data } = await api.get("/config/plan");
      setPlan(data);
    } catch {
      setPlan(null);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadPlan(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, [loadPlan]);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  // Correções de homologação (P1) — causa raiz do 403 logo após login: o
  // login() anterior confiava direto no `user` cru devolvido por
  // POST /auth/login, que nunca passa por get_current_user (só /auth/me
  // passa) — então nunca carregava financially_blocked. O primeiro render
  // pós-login achava a empresa "não bloqueada", montava Dashboard/Layout
  // normalmente, e só então as chamadas protegidas voltavam 403 (o
  // backend sempre esteve certo — só o estado em memória do frontend
  // estava desatualizado por um instante). Resolver /auth/me AQUI, antes
  // de qualquer navegação, garante que o primeiro render já decide o
  // destino certo (ProtectedRoute) sem nenhum "flash" de tela bloqueada.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("token", data.token);
    const me = await refreshUser();
    return me || data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("token");
    setUser(false);
    setPlan(null);
  };

  // Backstop imediato: qualquer 403 financeiro encontrado em QUALQUER
  // chamada da aplicação (não só no bootstrap) já marca o estado local
  // como bloqueado na hora — o ProtectedRoute reage no próximo render,
  // sem esperar o próximo ciclo de revalidação abaixo.
  useEffect(() => {
    setFinancialAccessBlockedHandler(() => {
      setUser((u) => (u && !u.financially_blocked ? { ...u, financially_blocked: true } : u));
    });
    return () => setFinancialAccessBlockedHandler(null);
  }, []);

  // P2 — revalidação em segundo plano enquanto bloqueado: pega tanto a
  // liberação por confiança (48h) quanto a baixa manual/confirmação do
  // provider, sem exigir logout/F5/reinício do backend (item 9 do pedido).
  // Só entra em polling quando de fato bloqueado — nunca sobrecarrega o
  // backend para as empresas em dia.
  const blocked = !!(user && user.financially_blocked);
  const refreshUserRef = useRef(refreshUser);
  refreshUserRef.current = refreshUser;
  useEffect(() => {
    if (!blocked) return;
    const iv = setInterval(() => { refreshUserRef.current(); }, BLOCKED_REVALIDATE_MS);
    return () => clearInterval(iv);
  }, [blocked]);

  // Camada central de checagem de recurso (Planos/Recursos, item 13) —
  // TODO componente que precisa decidir se mostra/esconde algo com base no
  // plano contratado deve usar hasFeature(codigo), nunca reimplementar essa
  // lógica localmente. Ocultar (não apenas desabilitar) é responsabilidade
  // de quem chama; esta função só responde sim/não.
  const hasFeature = useCallback((codigo) => {
    const resources = plan?.resources || [];
    const r = resources.find((x) => x.codigo === codigo);
    return !!(r && r.available);
  }, [plan]);

  // Camada central de checagem de PERMISSÃO — espelha exatamente
  // `has_perm` do backend (server.py): admin pode tudo; os demais perfis
  // somam o padrão do próprio perfil (`role_defaults`, vindo de
  // GET /permissions) com as permissões EXTRAS concedidas ao usuário
  // (`user.permissions`).
  //
  // CAUSA DA REGRESSÃO que isto corrige: as telas decidiam por ROLE
  // ("user.role === 'admin' || 'supervisor'"), então uma permissão
  // concedida individualmente — por exemplo `clients:create` para um
  // Analista — era aceita pelo backend mas NUNCA aparecia na tela. Toda
  // decisão de mostrar/esconder ação passa a usar hasPerm(codigo).
  // Enquanto o catálogo não carregou, só o `user.permissions` é
  // considerado — nunca libera nada a mais por engano.
  const [roleDefaults, setRoleDefaults] = useState({});
  useEffect(() => {
    if (!user) { setRoleDefaults({}); return; }
    let vivo = true;
    api.get("/permissions")
      .then(({ data }) => { if (vivo) setRoleDefaults(data?.role_defaults || {}); })
      .catch(() => { /* sem catálogo, cai nas permissões explícitas do usuário */ });
    return () => { vivo = false; };
  }, [user?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const hasPerm = useCallback((codigo) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    if ((roleDefaults[user.role] || []).includes(codigo)) return true;
    return (user.permissions || []).includes(codigo);
  }, [user, roleDefaults]);

  // Delta — ocultar envio integrado por WhatsApp em produção: APP_ENV vem
  // de /auth/me (única fonte oficial de ambiente — nunca detecção por
  // hostname aqui). `isProduction` fica false enquanto `user` ainda não
  // carregou (null) ou não está autenticado (false) — nunca esconde nada
  // por engano antes do carregamento nem em telas públicas.
  const appEnv = user ? user.app_env : null;
  const isProduction = appEnv === "production";

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, plan, hasFeature, hasPerm, refreshUser, appEnv, isProduction }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
